export const globalRoutePrefixes = ['/auth', '/dashboard', '/settings', '/admin', '/forbidden'] as const

export const publicLocalizedRoutes = [
  '/',
  '/matches',
  '/competitions',
  '/teams-players',
  '/preferences',
  '/history',
  '/legal/terms',
  '/legal/privacy',
  '/legal/disclaimer',
] as const

export const publicSitemapRoutes = [
  '/matches',
  '/zh-CN/matches',
  '/competitions',
  '/zh-CN/competitions',
  '/teams-players',
  '/zh-CN/teams-players',
  '/preferences',
  '/zh-CN/preferences',
  '/legal/terms',
  '/legal/privacy',
  '/legal/disclaimer',
  '/zh-CN/legal/terms',
  '/zh-CN/legal/privacy',
  '/zh-CN/legal/disclaimer',
] as const

const exactLocalizedRoutePaths = {
  '/': true,
  '/matches': true,
  '/competitions': true,
  '/teams-players': true,
  '/preferences': true,
  '/legal/disclaimer': true,
  '/legal/privacy': true,
  '/legal/terms': true,
  '/radar': true,
  '/radar/worldcup': true,
} satisfies Record<string, true>
const localizedRoutePrefixes = ['/history', '/research', '/teams', '/players', '/competitions'] as const

const routeLocalePrefixPattern = /^\/([a-z]{2}(?:-[A-Za-z]{2})?)(?=\/|$)/

export interface RouteLocalePrefix {
  locale: string
  pathWithoutLocale: string
}

export function getRouteLocalePrefix(pathname: string): RouteLocalePrefix | null {
  const match = routeLocalePrefixPattern.exec(pathname)
  if (!match) {
    return null
  }

  const pathWithoutLocale = pathname.replace(`/${match[1]}`, '') || '/'
  return {
    locale: match[1],
    pathWithoutLocale,
  }
}

export function stripRouteLocalePrefix(pathname: string): string {
  return getRouteLocalePrefix(pathname)?.pathWithoutLocale ?? pathname
}

export function isGlobalRoutePath(pathname: string): boolean {
  const path = stripRouteLocalePrefix(pathname)
  return globalRoutePrefixes.some(prefix => path === prefix || path.startsWith(`${prefix}/`))
}

export function isAuthRoutePath(pathname: string): boolean {
  const path = stripRouteLocalePrefix(pathname)
  return path === '/auth' || path.startsWith('/auth/')
}

export function shouldStripLocalePrefix(pathname: string): boolean {
  const prefix = getRouteLocalePrefix(pathname)
  if (!prefix) {
    return false
  }

  return isGlobalRoutePath(prefix.pathWithoutLocale) && !isAuthRoutePath(prefix.pathWithoutLocale)
}

export function shouldBypassLocaleMiddleware(pathname: string): boolean {
  return isGlobalRoutePath(pathname) && !isAuthRoutePath(pathname) && !getRouteLocalePrefix(pathname)
}

export function isLocalizedRoutePath(pathname: string): boolean {
  return isAuthRoutePath(pathname)
    || pathname in exactLocalizedRoutePaths
    || localizedRoutePrefixes.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`))
}
interface ResolveLocalizedRouteToOptions {
  locale?: string | null
  defaultLocale?: string
}

export function resolveLocalizedRouteTo(to: string, options: ResolveLocalizedRouteToOptions = {}): string {
  if (to.startsWith('/$locale')) {
    return to
  }

  if (isGlobalRoutePath(to) && !isAuthRoutePath(to)) {
    return to
  }
  const defaultLocale = options.defaultLocale ?? 'en'
  const shouldUseLocalePrefix = Boolean(options.locale && options.locale !== defaultLocale)

  if (to === '/') {
    return shouldUseLocalePrefix ? '/$locale' : '/'
  }

  return (isLocalizedRoutePath(to) || isAuthRoutePath(to)) && shouldUseLocalePrefix ? `/$locale${to}` : to
}
